<i18n src="./resources/locales.json"></i18n>

<template>
  <div>
    <v-alert class="offline-mode-indicator warning--text" dark :value="isOfflineModeEnabled">
      {{ $t('offlineMode') }}
    </v-alert>

    <v-app-bar
      id="app-toolbar"
      class="toolbar dark"
      :class="{ secondary: $vuetify.theme.dark, primary: !$vuetify.theme.dark }"
      :offline="isOfflineModeEnabled"
      height="64"
      app
      fixed
      clipped-left
    >
      <div aria-label="Brand logo">
        <v-img
          id="img-logo"
          :src="require('@/assets/xtiumcolor_x_mark.svg')"
          max-height="36"
          max-width="36"
          :dark="$vuetify.theme.dark"
        />
      </div>

      <EcsAgentState id="tour-agent-state" ref="agentState" class="ml-6" />
      <EcsAgentLoadAllowance id="tour-load-allowance" class="ml-2" />
      <EcsSelectAgentExtension class="ml-2" />

      <div v-if="currentUserSettings.preferencesSettings.showAppCurrentTime && selectedTimeZoneName" class="clock">
        <div class="label">{{ selectedTimeZoneName }}</div>
        {{ selectedTimeZoneTime }}
      </div>

      <v-spacer></v-spacer>

      <ecs-http-progressbar />

      <v-tooltip v-if="customerAIAssistanceBotURL" color="secondary" bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            id="tour-chatbot"
            dark
            role="button"
            :aria-label="$t('main.aiAssistance')"
            icon
            v-on="on"
            @click="toggleAIAssistance"
          >
            <v-icon>
              mdi-robot
            </v-icon>
          </v-btn>
        </template>
        <span v-once>{{ $t('main.aiAssistance') }}</span>
      </v-tooltip>

      <v-tooltip v-if="isSupervisor" color="secondary" bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            id="tour-assignments"
            dark
            role="button"
            :aria-label="$t('main.assignments')"
            icon
            v-on="on"
            @click="openAssignments"
          >
            <v-icon>
              mdi-clipboard-account
            </v-icon>
          </v-btn>
        </template>
        <span v-once>{{ $t('main.assignments') }}</span>
      </v-tooltip>

      <v-tooltip color="secondary" bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            id="tour-contacts"
            dark
            role="button"
            icon
            :aria-label="$t('main.directory')"
            :class="directoryIconClass"
            v-on="on"
            @click="toggleDirectory"
          >
            <v-icon>
              mdi-contacts
            </v-icon>
          </v-btn>
        </template>
        <span v-once>{{ $t('main.directory') }}</span>
      </v-tooltip>

      <v-tooltip color="secondary" bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            id="tour-callbacks"
            dark
            role="button"
            :aria-label="$t('main.callbacks')"
            icon
            :class="callbacksIconClass"
            v-on="on"
            @click="toggleCallbacksPanel"
          >
            <v-badge color="accent" :value="callbacksCount" overlap>
              <template v-slot:badge>
                {{ callbacksCount }}
              </template>
              <v-icon>mdi-phone-return</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span v-once>{{ $t('main.callbacks') }}</span>
      </v-tooltip>

      <v-tooltip v-if="isSupervisor" color="secondary" bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            id="tour-reports"
            dark
            role="button"
            :aria-label="$t('main.reports')"
            icon
            v-on="on"
            @click="redirectToReports"
          >
            <v-icon>
              mdi-chart-areaspline
            </v-icon>
          </v-btn>
        </template>
        <span v-once>{{ $t('main.reports') }}</span>
      </v-tooltip>

      <v-tooltip color="secondary" bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            id="tour-notifications"
            dark
            role="button"
            icon
            :aria-label="$t('main.notifications')"
            :class="notificationsIconClass"
            v-on="on"
            @click="toggleNotificationsPanel"
          >
            <v-badge color="accent" :value="getUnreadSystemNotifications" overlap>
              <template v-slot:badge>
                {{ getUnreadSystemNotifications }}
              </template>
              <v-icon>mdi-bell</v-icon>
            </v-badge>
          </v-btn>
        </template>
        <span v-once>{{ $t('main.notifications') }}</span>
      </v-tooltip>

      <!-- Agent Info & Image Profile -->
      <v-tooltip color="secondary" bottom>
        <template v-slot:activator="{ on }">
          <div id="tour-agent-profile" v-on="on">
            <EcsAgentInfo id="agent_info">
              <template v-slot:actions>
                <v-spacer />
                <v-btn role="button" :aria-label="$t('buttons.signout')" color="primary" @click="userLogout">
                  <span v-once>{{ $t('buttons.signout') }}</span>
                </v-btn>
              </template>
            </EcsAgentInfo>
          </div>
        </template>
        <span v-once>{{ $t('main.profile') }}</span>
      </v-tooltip>
    </v-app-bar>

    <EcsContactsDirectory ref="directory_panel" />
    <EcsCallbacksPanel ref="callbacks_panel" />
    <EcsAgentNotifications ref="notifications_panel" />
    <EcsAssignmentsDialog ref="assignments_dialog" />
    <EcsAIAssistancePanel ref="ai_assistance_panel" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import EcsAgentLoadAllowance from '@/components/ecs-agent-load-allowance/EcsAgentLoadAllowance';
import EcsAgentInfo from '@/components/ecs-agent-info/EcsAgentInfo';
import EcsAgentState from '@/components/ecs-agent-state/EcsAgentState';
import EcsContactsDirectory from '@/components/ecs-contacts-directory/EcsContactsDirectory';
import EcsCallbacksPanel from '@/components/ecs-callbacks-panel/EcsCallbacksPanel';
import EcsAgentNotifications from '@/components/ecs-agent-notifications/EcsAgentNotifications';
import EcsAssignmentsDialog from '@/components/ecs-assignments-dialog/EcsAssignmentsDialog';
import { AGENT_STATE } from '@/constants/agent/constants';
import EcsSelectAgentExtension from '@/components/ecs-select-agent-extension/EcsSelectAgentExtension';
import EcsHttpProgressbar from '@/components/ecs-http-progressbar/EcsHttpProgressbar';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';
import EcsAIAssistancePanel from '@/components/ecs-ai-assistance-panel/EcsAIAssistancePanel.vue';

export default {
  name: 'EcsAppHeader',

  components: {
    EcsAIAssistancePanel,
    EcsHttpProgressbar,
    EcsSelectAgentExtension,
    EcsAgentInfo,
    EcsAgentLoadAllowance,
    EcsAgentState,
    EcsContactsDirectory,
    EcsCallbacksPanel,
    EcsAgentNotifications,
    EcsAssignmentsDialog
  },

  mixins: [EcsTimeFormatter],

  data() {
    return {
      callbacksIconClass: '',
      directoryIconClass: '',
      notificationsIconClass: '',
      socketReconnectionAttemptCounter: 0,
      isAgentInfoOpen: false,
      currentDeviceTime: '',
      selectedTimeZoneTime: '',
      selectedTimeZoneName: '',
      currentTimeRef: null
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent', 'isSupervisor']),

    ...mapGetters('INTERACTIONS', ['getPendingInteractions']),

    ...mapGetters('NOTIFICATIONS', [
      'getCallbacks',
      'getTotalInteractionsWithNotifications',
      'getUnreadSystemNotifications'
    ]),

    ...mapGetters('SETTINGS', ['currentUserSettings', 'isOfflineModeEnabled', 'applicationSettings']),

    ...mapGetters('ORGANIZATION', ['orgReportingServerUrl', 'customerAIAssistanceBotURL']),

    callbacksCount() {
      return this.getCallbacks.length;
    }
  },

  watch: {
    getUnreadSystemNotifications: {
      handler() {
        this.checkTabNotification();
      },
      deep: true
    }
  },

  mounted() {
    this.$root.$on('close-side-panels', this.closeAllPanels);
    this.$root.$on('open-callbacks-panel', this.toggleCallbacksPanel);
    this.$root.$on('open-notifications-panel', this.toggleNotificationsPanel);
    this.$root.$on('open-directory-panel', this.toggleDirectory);
    this.$root.$on('change-favicon', this.checkTabNotification);
    this.currentTimeRef = setInterval(this.setCurrentTime, 1000);
  },

  beforeDestroy() {
    this.$root.$off('close-side-panels', this.closeAllPanels);
    this.$root.$off('open-callbacks-panel', this.toggleCallbacksPanel);
    this.$root.$off('open-notifications-panel', this.toggleNotificationsPanel);
    this.$root.$off('open-directory-panel', this.toggleDirectory);
    this.$root.$off('change-favicon', this.checkTabNotification);
    clearInterval(this.currentTimeRef);
  },

  sockets: {
    reconnect() {
      this.socketReconnectionAttemptCounter = 0;
    },

    reconnecting() {
      this.socketReconnectionAttemptCounter++;
    }
  },

  methods: {
    ...mapActions('NOTIFICATIONS', ['updateUnreadNotifications']),

    setCurrentTime() {
      const displayFormat =
        this.applicationSettings && this.applicationSettings.timeFormat.replace('mm', 'mm:ss').replace('hh', 'h');
      this.selectedTimeZoneName = (this.getCurrentTimeZone() || {}).name;
      this.selectedTimeZoneTime = this.getCurrentTimeFormatted(displayFormat || 'HH:mm:ss');
    },

    closeAllPanels() {
      if (this.$refs.callbacks_panel) {
        this.$refs.callbacks_panel.setOpen(false);
      }
      if (this.$refs.directory_panel) {
        this.$refs.directory_panel.opened = false;
      }
      if (this.$refs.notifications_panel) {
        this.$refs.notifications_panel.setOpen(false);
      }
      if (this.$refs.ai_assistance_panel) {
        this.$refs.ai_assistance_panel.close();
      }
      this.updateSidePanelIcons();
    },

    updateSidePanelIcons() {
      this.callbacksIconClass = this.$refs.callbacks_panel && this.$refs.callbacks_panel.isOpened() ? 'active' : '';
      this.directoryIconClass = this.$refs.directory_panel && this.$refs.directory_panel.opened ? 'active' : '';
      this.notificationsIconClass =
        this.$refs.notifications_panel && this.$refs.notifications_panel.isOpened() ? 'active' : '';
    },

    toggleCallbacksPanel() {
      this.$refs.callbacks_panel.setOpen(!this.$refs.callbacks_panel.isOpened());
      this.$refs.directory_panel.opened = false;
      this.$refs.notifications_panel.setOpen(false);
      this.$refs.ai_assistance_panel.close();
      this.updateSidePanelIcons();
    },

    toggleDirectory() {
      this.$refs.directory_panel.opened = !this.$refs.directory_panel.opened;
      this.$refs.callbacks_panel.setOpen(false);
      this.$refs.notifications_panel.setOpen(false);
      this.$refs.ai_assistance_panel.close();
      this.updateSidePanelIcons();
    },

    toggleAIAssistance() {
      this.$refs.ai_assistance_panel.toggle();
      this.$refs.directory_panel.opened = false;
      this.$refs.callbacks_panel.setOpen(false);
      this.$refs.notifications_panel.setOpen(false);
      this.updateSidePanelIcons();
    },

    toggleNotificationsPanel() {
      const isNotificationsPanelOpen = this.$refs.notifications_panel.isOpened();
      if (!isNotificationsPanelOpen) {
        this.updateUnreadNotifications(0);
      }
      this.$refs.notifications_panel.setOpen(!isNotificationsPanelOpen);
      this.$refs.callbacks_panel.setOpen(false);
      this.$refs.ai_assistance_panel.close();
      this.$refs.directory_panel.opened = false;
      this.updateSidePanelIcons();
    },

    async userLogout() {
      const signout = await this.$refs.agentState.changeAgentState({ id: AGENT_STATE.offline });
      if (signout) {
        this.$emit('user-logout-request');
      } else {
        this.$root.$emit('snackbar-message', {
          message: 'Your agent can not go offline at this moment. Please complete your active interactions.',
          type: 'info'
        });
      }
    },

    checkTabNotification() {
      if (
        this.getPendingInteractions.length > 0 ||
        this.getTotalInteractionsWithNotifications > 0 ||
        this.getUnreadSystemNotifications > 0
      ) {
        this.$faviconUpdater.updateFavicon(true);
      } else {
        this.$faviconUpdater.updateFavicon(false);
      }
    },

    redirectToReports() {
      window.open(this.orgReportingServerUrl);
    },

    openAssignments() {
      this.$refs.assignments_dialog.open();
    }
  }
};
</script>
<style scoped>
#app-toolbar {
  box-shadow: none;
}

.toolbar {
  z-index: 119;
  margin-top: 0 !important;
}

[offline].toolbar {
  margin-top: 30px !important;
}

#img-logo.theme--light {
  filter: saturate(0) brightness(5);
}

#agent_info {
  cursor: pointer;
  padding-left: 8px;
}

.offline-mode-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 30px;
  color: #f89e7e;
  font-size: 12px;
  text-transform: uppercase;
  padding: 0;
  margin: 0;
  border-radius: 0;
}

.offline-mode-indicator.v-icon {
  font-size: 16px;
}

[class='~dark'].v-btn.active:before {
  color: var(--v-primary-base);
  opacity: 0;
}

.v-btn.active:before {
  opacity: 0.25;
}

.clock {
  margin: 8px;
  padding: 8px 16px;
  height: 52px;
  border-radius: 4px;
  cursor: pointer;
  color: #fff;
}

.clock:hover {
  background-color: #0004;
}

.clock .label {
  font-size: 12px;
  opacity: 0.5;
}
</style>
